import TextArea from "components/atoms/input/TextArea";
import BigButton from "components/atoms/buttons/BigButton";
import AddressInput from "components/atoms/input/address/AddressInput";
import AddressMap from "components/atoms/input/address/AddressMap";
import type { Option } from "utils/options";
import {
  RecorderConfig,
  RecorderEditInfo,
} from "api/interfaces/recorderInterface.interface";
import { UPDATE_TYPE } from "./EditListContainer";
import SelectComponent from "components/atoms/select/SelectComponent";
import {
  LEVEL_TYPE,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { ValidateWord } from "components/atoms/text/Text";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { convertType } from "utils/functions";
import { deRegistrationSupportCheck } from "utils/RecorderUtil";
import Deactivate from "components/blocks/modal/deactivate";
import { useMemo, useState } from "react";
import ThumbnailChangeModal, {
  ThumbnailModalType,
} from "./thumbnail/ThumbnailChangeModal";
import * as s from "./EditRecorderStyled";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";

interface Props {
  type: string;
  mergedCount: number;
  isSystem: boolean;
  isLoaded: boolean;
  selected: google.maps.LatLngLiteral;
  onChangeLocationSelect: (
    address: google.maps.LatLngLiteral,
    addressString: string
  ) => void;
  onChangeRecordInfo: (
    type: UPDATE_TYPE,
    value: number | string | boolean | string[]
  ) => void;
  onChangeAddress: (e: any) => void;
  data: RecorderEditInfo;
  onSave: () => void;
  imgFile: any;
  saveImgFile: (e: any) => void;
  onDeleteImgFile: () => void;
  imgRef: any;
  groupData: Option[];
  groupIsFetching: boolean;
  selectGroup: string[];
  mapRef: any;
  onMarkerChange: (
    address: google.maps.LatLngLiteral,
    addressString: string
  ) => void;
  onClickDelete: () => void;
  validate: Validate;
  validateMsg: ValidateMsg;
  registeredDate: string;
  disabled: boolean;
  deleteDisabled: boolean;
  mergedSystemName?: string;
  isLoading: boolean;
  disableDelete?: boolean;
  isDetached?: boolean;
}

export function EditListPresenter(props: Props): JSX.Element {
  const [thumbnailModalType, setThumbnailModalType] =
    useState<ThumbnailModalType | null>(null);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const deactiveDisabled = useMemo(() => {
    const typeString = convertType(props.data);
    if (props.isSystem) {
      return false;
    }
    return !deRegistrationSupportCheck(typeString, props.data.firmwareVersion);
  }, [props.data, props.isSystem]);
  const isMergedSystem = useMemo(
    () => props.isSystem || props.mergedCount > 1,
    [props.isSystem, props.mergedCount]
  );
  const onClickCancel = () => {
    window.history.back();
  };

  const onClickChangeThumbnail = () => {
    setThumbnailModalType(ThumbnailModalType.SELECT);
  };
  const closeThumbnailModal = () => setThumbnailModalType(null);
  const onClickUploadFromDevice = () => {
    if (props.imgRef?.current) {
      props.imgRef.current.click();
    }
  };

  return (
    <s.PageWrapper>
      <s.FormCard>
        <s.TopWrapper>
          <li>
            <div className="title">
              {props.isSystem ? "System Name" : "Recorder Name"}
            </div>
            <div className="content">{props.data.name}</div>
          </li>
          <li>
            <div className="title">Version</div>
            <div className="content">{props.data.firmwareVersion}</div>
          </li>
          <li>
            <div className="title">Register Date</div>
            <div className="content">{props.registeredDate}</div>
          </li>
        </s.TopWrapper>

        <s.Divider />

        <s.FormLayout>
          <s.MainGrid>
            <s.Field>
              <s.Label>
                Location <s.Required>*</s.Required>
              </s.Label>
              <AddressInput
                isLoaded={props.isLoaded}
                defaultValue={props.data.location.location}
                value={props.data.location.location}
                onChange={props.onChangeAddress}
                onChangeLocationSelect={props.onChangeLocationSelect}
                data-testid="recorders-edit-list-address-input-change-address"
              />

              {props.validate.address && (
                <ValidateWord data-testid="recorders-edit-list-validate-address">
                  {props.validateMsg.address}
                </ValidateWord>
              )}

              <s.MapBox>
                <AddressMap
                  isLoaded={props.isLoaded}
                  selected={props.selected}
                  ref={props.mapRef}
                  onMarkerChange={props.onMarkerChange}
                />
              </s.MapBox>
            </s.Field>

            <s.RightColumn>
              {selectedAccount.accountLevel === LEVEL_TYPE.EU && (
                <s.Field>
                  <s.Label>Recorder Group</s.Label>
                  <SelectComponent
                    mode="multiple"
                    options={props.groupData}
                    loading={props.groupIsFetching}
                    value={props.selectGroup}
                    onChange={(value) =>
                      props.onChangeRecordInfo(
                        UPDATE_TYPE.RECORDER_GROUP,

                        (value as string[]) || []
                      )
                    }
                  />
                </s.Field>
              )}

              <s.Field>
                <s.Label>
                  Recorder Thumbnail <s.Required>*</s.Required>
                </s.Label>
                <s.ThumbBox>
                  <s.HiddenFileInput
                    type="file"
                    accept="image/png, image/jpg, image/jpeg"
                    ref={props.imgRef}
                    onChange={props.saveImgFile}
                    data-testid="recorders-edit-list-hidden-file-input-save-img-file"
                  />

                  {props.imgFile !== "" && (
                    <img src={props.imgFile} alt="Recorder_Thumbnail" />
                  )}

                  <s.ThumbActionButton
                    type="button"
                    onClick={onClickChangeThumbnail}
                    data-testid="recorders-edit-list-thumb-action-button-click-change-thumbnail"
                  >
                    Change
                  </s.ThumbActionButton>
                </s.ThumbBox>
              </s.Field>
            </s.RightColumn>
          </s.MainGrid>

          <s.Field>
            <s.Label>Description</s.Label>
            <TextArea
              placeholder="Please enter a description"
              defaultValue={props.data.description}
              onChange={(e) =>
                props.onChangeRecordInfo(UPDATE_TYPE.DESC, e.target.value)
              }
              data-testid="recorders-edit-list-textarea-change-record-info"
            />
          </s.Field>
        </s.FormLayout>
      </s.FormCard>

      <s.BottomButtonBar>
        {!deactiveDisabled && (
          <RBACWrapper requiredPermissions={"manageRecordersAndGroups"}>
            {props.isSystem ? (
              <Deactivate
                title={
                  <>
                    Deleting the system will deregister it from myDW,
                    <br /> including all merged servers.
                    <br />
                    Are you sure you want to delete this system?
                  </>
                }
                subTitle={
                  isMergedSystem
                    ? props.data.mergedSystemName ?? props.data.name
                    : props.data.name
                }
                onDeactive={props.onClickDelete}
                disabled={props.deleteDisabled || props.disabled}
              />
            ) : (
              (!isMergedSystem || props.isDetached) && (
                <Deactivate
                  title="Are you sure you want to delete this recorder?"
                  subTitle={props.data.name}
                  onDeactive={props.onClickDelete}
                  disabled={props.deleteDisabled || props.disabled}
                />
              )
            )}
          </RBACWrapper>
        )}

        <BigButton
          buttonType="cancel"
          label="Cancel"
          onClickBtn={onClickCancel}
          data-testid="recorders-edit-list-button-click-cancel"
        />

        <BigButton
          label="Save"
          disabled={props.disabled}
          onClickBtn={props.onSave}
          data-testid="recorders-edit-list-button-save"
        />
      </s.BottomButtonBar>

      <ThumbnailChangeModal
        type={thumbnailModalType}
        imgFile={props.imgFile}
        onClose={closeThumbnailModal}
        onSelectType={setThumbnailModalType}
        onUploadFromDevice={onClickUploadFromDevice}
      />
    </s.PageWrapper>
  );
}
